import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router';

// Design System Components
import Button from 'Components/DesignSystem/Button/Button';
import Divider from 'Components/DesignSystem/Divider/Divider';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import Search from 'Components/DesignSystem/Search/Search';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Delete, MoreVertical } from 'Components/DesignSystem/Iconography/Icons';

// Module Components
import {
  ApplicationBadges,
  BlockedDeleteDialog,
  DeleteCardDialog,
  NewCardDialog,
} from '../components/listing';

// Contexts
import { useProductCards } from '../contexts';
import { isFieldError } from '../services';

// Selectors
import { selectDeleteBlockers } from '../selectors';

// Constants
import { SHOW_COUNT_OPTIONS, STRINGS, cardEditorRoute, formatUpdatedAt } from '../constants';

// Types
import { SortDirection, TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';
import { CardListingRow } from '../types';

/** R23's 10 / 25 / 50 / 100, as the DS Dropdown wants them. */
const PAGE_SIZE_OPTIONS = SHOW_COUNT_OPTIONS.map((count) => ({ value: count, label: String(count) }));

/**
 * The Product Cards listing: the table, the name search, the pager, and the
 * create and delete flows (spec R16–R27).
 *
 * The table IS Guide's table (`ISRProjectTable`, design-system § 87) — the DS
 * `Table` with `striped hoverable loading`, its own skeleton rows on first load
 * and a dimmed "Refreshing" refetch, `accent-bold` link ink on the name, `meta`
 * ink on the dates, an unlabelled 48px actions column holding a kebab that opens
 * a DS `Menu` portalled to the body, and Guide's footer: a DS `Dropdown` for
 * rows-per-page beside the DS `Pagination`. What sits underneath is untouched:
 * search, sort and pagination are still SERVER queries through `useProductCards`,
 * and the create / delete / blocked-delete flows are the same dialogs.
 */
const ProductCardsListView = () => {
  const {
    rows,
    isLoading,
    searchTerm,
    isSearchPending,
    setSearchTerm,
    showCount,
    setShowCount,
    page,
    setPage,
    total,
    sortState,
    setSort,
    hasNoSearchMatches,
    createCard,
    deleteCard,
  } = useProductCards();
  const navigate = useNavigate();

  const [isCreating, setIsCreating] = useState(false);
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
  // The server's refusal of the typed name, shown under the name box. Cleared
  // on the next keystroke so a stale message cannot outlive the name it was
  // about.
  const [createError, setCreateError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // The row kebab's menu — Guide's § 81 pattern: the DS Table wrapper is an
  // overflow container, so an absolutely-positioned popover inside a cell would
  // clip at the table edge; the menu portals to the body, fixed, hanging leftward
  // from the kebab. Keyed by the row's id, never its index.
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const handleSort = (columnId: string, direction: SortDirection) => {
    // The hook returns to page 1: re-sorting reorders every page's contents.
    setSort({ columnId, direction });
  };

  const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
    setOpenMenuRowId((previous) => (previous === rowId ? null : rowId));
  };

  useEffect(() => {
    // A fixed-position menu detaches from its row the moment anything scrolls,
    // so scrolling closes it. Outside-click and Escape are the DS Menu's own
    // job (`onDismiss`).
    if (openMenuRowId === null) {
      return undefined;
    }
    const close = () => setOpenMenuRowId(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [openMenuRowId]);

  const pendingDeleteCard = useMemo(
    () => rows.find((row) => row.id === pendingDeleteId) ?? null,
    [rows, pendingDeleteId]
  );
  const deleteBlockers = pendingDeleteCard ? selectDeleteBlockers(pendingDeleteCard) : [];

  // Two emptinesses (2026-09-16). No cards in this Environment at all: the EmptyState
  // stands ALONE, as Guide's does — column headers would promise a table that is not
  // there and the sort chevron would be a control over nothing. A search that matches
  // nothing is a filter on a REAL table: the headers keep the context of what is being
  // filtered, and the compact state sits inside the body. `total`, not `rows.length`:
  // a page left past the end while the total shrinks is the provider's clamp, not
  // an empty catalogue.
  const hasNoCards = !isLoading && total === 0 && !hasNoSearchMatches;

  // Both handlers hold their dialog open and disabled while the request is in
  // flight — the re-entry guards are what make a double-click send ONE request,
  // not two (a second card / a second DELETE, live risk before 2026-08-18).
  const handleCreate = async (name: string) => {
    if (isSubmittingCreate) {
      return;
    }
    setIsSubmittingCreate(true);
    setCreateError(null);
    try {
      const created = await createCard(name);
      if (created) {
        setIsCreating(false);
        navigate(cardEditorRoute(created.id));
      }
    } catch (error) {
      // The dialog stays open with the typed name intact — closing would
      // discard it — and now SAYS why. The server's own sentence is used
      // verbatim for a refusal that names a field ("A card named 'abcd'
      // already exists in this channel."), because it is already written for a
      // person and it names the card. Anything else gets the generic line: an
      // unexpected message from a transport failure is not something to show.
      setCreateError(
        isFieldError(error) ? error.message : STRINGS.newCardDialog.createFailed
      );
    } finally {
      setIsSubmittingCreate(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (isDeleting || !pendingDeleteCard) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteCard(pendingDeleteCard.id);
    } finally {
      setIsDeleting(false);
      setPendingDeleteId(null);
    }
  };

  const renderRowActions = (row: CardListingRow) => {
    const isOpen = openMenuRowId === row.id;
    const menuItems: MenuItem[] = [
      {
        id: 'delete',
        label: STRINGS.listing.deleteAction,
        icon: <Delete size={16} aria-hidden="true" />,
        tone: 'danger',
        'data-testid': `product-cards-row-menu-delete-${row.id}`,
        onClick: () => {
          setOpenMenuRowId(null);
          setPendingDeleteId(row.id);
        },
      },
    ];
    return (
      <>
        <IconButton
          variant="subtle"
          size="sm"
          aria-label={`${STRINGS.listing.moreActions}: ${row.name}`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          data-testid={`product-cards-row-overflow-${row.id}`}
          onClick={(event) => handleOverflowClick(event, row.id)}
        >
          <MoreVertical size={16} aria-hidden="true" />
        </IconButton>
        {isOpen
          ? ReactDOM.createPortal(
              /* `tw-z-popover` — the § 304 rung for a portalled anchored surface
                 (Guide's older `tw-z-[9999]` predates the ladder). */
              <div
                className="tw-fixed tw-z-popover tw-w-52"
                style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
              >
                <Menu
                  variant="compact"
                  items={menuItems}
                  data-testid={`product-cards-row-overflow-menu-${row.id}`}
                  onDismiss={() => setOpenMenuRowId(null)}
                />
              </div>,
              document.body
            )
          : null}
      </>
    );
  };

  // Guide's column recipe: percentage widths, plain values where the DS cell
  // already inks them, `meta` for the dates, and an unlabelled 48px actions slot.
  const columns: TableColumn<CardListingRow>[] = [
    {
      id: 'name',
      header: STRINGS.listing.columns.name,
      accessor: 'name',
      sortable: true,
      width: '25%',
      render: (_value, row) => (
        <Link
          to={cardEditorRoute(row.id)}
          title={row.name}
          data-testid={`product-cards-row-name-${row.id}`}
          className="tw-block tw-truncate tw-text-accent-bold hover:tw-underline"
        >
          {row.name}
        </Link>
      ),
    },
    {
      id: 'referencedIn',
      header: STRINGS.listing.columns.referencedIn,
      accessor: 'references',
      width: '22%',
      render: (_value, row) => <ApplicationBadges references={row.references} />,
    },
    {
      id: 'createdBy',
      header: STRINGS.listing.columns.createdBy,
      accessor: 'createdBy',
      width: '16%',
      render: (_value, row) => row.createdBy || STRINGS.listing.noReferences,
    },
    {
      id: 'createdAt',
      header: STRINGS.listing.columns.createdAt,
      accessor: 'createdAt',
      sortable: true,
      cellType: 'meta',
      width: '13%',
      render: (_value, row) => (row.createdAt ? formatUpdatedAt(row.createdAt) : STRINGS.listing.noReferences),
    },
    {
      id: 'updatedAt',
      header: STRINGS.listing.columns.updatedAt,
      accessor: 'updatedAt',
      sortable: true,
      cellType: 'meta',
      width: '13%',
      render: (_value, row) => formatUpdatedAt(row.updatedAt),
    },
    {
      id: 'actions',
      header: '',
      accessor: 'id',
      width: '48px',
      align: 'right',
      render: (_value, row) => renderRowActions(row),
    },
  ];

  return (
    <div id="product-cards-page" className="tw-max-w-full">
      {/* The list-page header recipe (design-system §§ 264–273, the same row Guide,
          Lists, Compatibility, Manage Party and Category List read): title · search ·
          elastic gap · action. The search filters the grid below, so it answers
          "what am I looking at" and sits with the TITLE — in the action cluster it
          read as a second action (2026-09-16 screenshot). `justify-between` is gone
          with it: that is a two-child idiom, and it re-spread the row the moment the
          search became a third child. The one `tw-flex-grow` spacer says which gap
          is elastic. DOM order is title, search, Create — tab order unchanged. */}
      <div className="tw-flex tw-items-center tw-mb-2" data-testid="product-cards-header">
        <Typography variant="display" as="h1" className="tw-text-ink" data-testid="product-cards-page-title">
          {STRINGS.listing.pageTitle}
        </Typography>
        {/* 288px (`tw-w-72`) and `size="md"` are what the other list headers search
            at, so the row sits on ONE rung of the control grid beside the Button.
            Not rendered while the Environment has no cards at all: a search over
            nothing filters nothing — the same inert control as the pager and the
            sort chevron the empty state already dropped. `hasNoCards` is false while
            loading (so a first paint does not repaint the header on every page
            load) and false with a term typed (the no-match state, where the box is
            the way out). Title and Create do not move when it goes. */}
        {!hasNoCards && (
          <div className="tw-w-72 tw-min-w-0 tw-ms-4">
            <Search
              placeholder={STRINGS.listing.searchPlaceholder}
              value={searchTerm}
              onChange={setSearchTerm}
              /* § 288 — `isLoading` is the request; `isSearchPending` is the debounce
                 in front of it (the hook derives it — the trimmed term the query
                 carries has not caught up with the typed one yet). */
              loading={isLoading || isSearchPending}
              loadingLabel="Searching cards"
              variant="bordered"
              size="md"
              data-testid="product-cards-search-input"
            />
          </div>
        )}
        <div className="tw-flex tw-flex-grow" />
        {/* `shrink-0`: the search absorbs a narrow viewport, never the CTA. */}
        <Button
          variant="primary"
          showIcon={false}
          className="tw-shrink-0"
          onClick={() => setIsCreating(true)}
          data-testid="product-cards-create-btn"
        >
          {STRINGS.listing.newCardButton}
        </Button>
      </div>
      {/* The rule under the header that Guide (`isr-header-divider`) and Lists
          (`lists-header-divider`) draw: 8px above it, 12px below to whatever the body
          is — table or the standalone empty state, neither of which carries a top
          margin of its own. Asked for by eye, 2026-09-16. */}
      <Divider className="tw-mb-3" data-testid="product-cards-header-divider" />

      {hasNoCards ? (
        /* The DS empty-state grammar (`EmptyState` + `action`, design-system § 64): the
           page-level illustration and the ONE control that fixes the emptiness — the
           same moment Guide renders under its header. It was a `body-primary` sentence
           in `ink-tertiary` inside the table telling the user to go and find "+ New
           Card" (2026-09-16 screenshot). */
        <EmptyState
          illustration={<NoDataIllustration />}
          title={STRINGS.listing.emptyTitle}
          description={STRINGS.listing.emptyDescription}
          action={
            <Button
              variant="primary"
              showIcon={false}
              onClick={() => setIsCreating(true)}
              data-testid="product-cards-empty-create-btn"
            >
              {STRINGS.listing.newCardButton}
            </Button>
          }
          data-testid="product-cards-empty-state"
        />
      ) : (
        /* Guide's table props verbatim (§ 87): `striped hoverable loading`. `loading`
           is the DS Table's own treatment — skeleton rows under the real headers on a
           first load, dim + "Refreshing" over the rows it already has on a refetch —
           so no separate full-width shimmer is swapped in for it any more. */
        <Table<CardListingRow>
          /* § 308 — bound the grid so `<main>` stops scrolling the page out from under
             it; see `pageGridHeight.ts` for the measured reserve. `stickyHeader` needs
             the bound: sticky resolves against a scrolling ancestor, so the two ship as
             a pair and removing either silently restores the bug. */
          stickyHeader
          maxHeight={PAGE_GRID_MAX_HEIGHT}
          columns={columns}
          data={rows}
          rowKey="id"
          striped
          hoverable
          loading={isLoading}
          sortState={sortState}
          onSort={handleSort}
          data-testid="product-cards-table"
          /* The fix for a no-match search is the search box the user is already in, so
             this is the compact `sm` rung with no illustration and no Create. Any other
             empty body here is the transient page-clamp, and the Table's own default
             says so far better than a wrong sentence about the search would. */
          emptyContent={
            hasNoSearchMatches ? (
              <EmptyState
                size="sm"
                title={STRINGS.listing.noSearchResults}
                description={STRINGS.listing.noSearchResultsHint}
                data-testid="product-cards-empty-state"
              />
            ) : undefined
          }
        />
      )}

      {/* Guide's footer (§ 87): the DS Dropdown for rows-per-page beside the DS
          Pagination, right-aligned at `tw-gap-3`. It follows the DS `Pagination` rule
          (nothing to page → renders nothing) and Guide's gate: a rows-per-page control
          over zero rows controls nothing. `total`, not `!isLoading`: during a re-fetch
          the last answer's total keeps the footer where it was, so the row does not
          blink on every keystroke. */}
      {total > 0 && (
        <div className="tw-py-2" data-testid="product-cards-pager">
          <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap">
            <Dropdown
              label=""
              aria-label={STRINGS.listing.rowsPerPage}
              size="sm"
              className="tw-w-[84px]"
              options={PAGE_SIZE_OPTIONS}
              value={showCount}
              onChange={(value) => setShowCount(Number(value))}
              data-testid="product-cards-page-size"
            />
            <Pagination
              page={page}
              totalItems={total}
              pageSize={showCount}
              onPageChange={setPage}
              size="sm"
              data-testid="product-cards-pagination"
            />
          </div>
        </div>
      )}

      <NewCardDialog
        error={createError}
        onNameChange={() => setCreateError(null)}
        isOpen={isCreating}
        isSubmitting={isSubmittingCreate}
        onCancel={() => setIsCreating(false)}
        onCreate={handleCreate}
      />

      <DeleteCardDialog
        isOpen={pendingDeleteCard !== null && deleteBlockers.length === 0}
        cardName={pendingDeleteCard?.name ?? ''}
        isDeleting={isDeleting}
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={handleConfirmDelete}
      />

      <BlockedDeleteDialog
        isOpen={pendingDeleteCard !== null && deleteBlockers.length > 0}
        cardName={pendingDeleteCard?.name ?? ''}
        blockers={deleteBlockers}
        onDismiss={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default ProductCardsListView;
